import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, TableCell, TableRow, Tooltip } from '@mui/material';

import { PublicOfferV2 } from '@luxuryescapes/contract-public-offer';

import RankingInfo from '~/components/Common/RankingInfo';
import { FlexTableCell } from '~/components/Content/SearchRanking/FlexTableCell';

import OffersService from '~/services/OffersService';
import PublicOfferService from '~/services/PublicOfferService';
import SearchService, { EvVariant, GlobalRanks, HotelAdminScore } from '~/services/SearchService';

import currencyFormatter from '~/utils/currencyFormatter';
import isUUID from '~/utils/isUUID';

import { headerColumnSpan } from './SearchHotelHead';

const OFFER_TYPE_LABELS: Record<string, string> = {
  hotel: 'Flash',
  tactical_ao_hotel: 'LPC',
  bedbank_hotel: 'LPP',
};

type LegacyHotelAdminScore = HotelAdminScore & {
  numberOfViews?: number;
  weightedNumberOfViews?: number;
  totalSales?: number;
  totalMargin?: number;
  weightedTotalMargin?: number;
  expectedValue?: number;
  calculatedScore?: number;
  global_mpv_sales?: number;
};

function firstDefinedNumber(...values: Array<number | undefined>): number {
  const value = values.find((candidate) => typeof candidate === 'number' && !Number.isNaN(candidate));
  return value ?? 0;
}

function formatFinalScore(overrideScore: number, salesMpv: number): string {
  if (overrideScore > 0) {
    return overrideScore.toFixed(1);
  }

  return salesMpv.toFixed(3);
}

function readHotelRankingScore(score: HotelAdminScore) {
  const legacyScore = score as LegacyHotelAdminScore;
  const salesAmount = firstDefinedNumber(score.salesAmount, legacyScore.totalSales);
  const marginAmount = firstDefinedNumber(score.marginAmount, legacyScore.totalMargin);

  return {
    viewsCount: firstDefinedNumber(score.viewsCount, legacyScore.numberOfViews),
    weightedViewsCount: firstDefinedNumber(score.weightedViewsCount, legacyScore.weightedNumberOfViews),
    salesCount: firstDefinedNumber(score.salesCount),
    weightedSalesCount: firstDefinedNumber(score.weightedSalesCount),
    salesAmount,
    marginAmount,
    weightedMarginAmount: firstDefinedNumber(score.weightedMarginAmount, legacyScore.weightedTotalMargin),
    mpv: firstDefinedNumber(score.mpv, legacyScore.expectedValue),
    salesMpv: firstDefinedNumber(score.sales_mpv, legacyScore.calculatedScore),
    globalSalesMpv: firstDefinedNumber(score.global_sales_mpv, legacyScore.global_mpv_sales),
    overrideScore: firstDefinedNumber(score.overrideScore),
    ctr: firstDefinedNumber(score.ctr),
    info: score.info,
    marginPercentage: salesAmount > 0 ? ((marginAmount / salesAmount) * 100).toFixed(2) + '%' : 'N/A',
  };
}

interface IExpectedValueData {
  views: string;
  weightedViews: string;
  salesCount: string;
  weightedSalesCount: string;
  salesAmount: string;
  marginAmount: string;
  weightedMarginAmount: string;
  marginPercentage: string;
  mpv: string;
  salesMpv: string;
  globalSalesMpv: string;
  overrideScore: string;
  finalScore: string;
  ctr: string;
  info?: string;
  globalRank?: number | 'n/a';
}

interface SearchHotelRowProps {
  offerId: string;
  region: string;
  brand: string;
  isLuxPlusMember: boolean;
  showDetailed: boolean;
}

function SearchHotelRow({ offerId, region, brand, isLuxPlusMember, showDetailed }: SearchHotelRowProps) {
  const isMountedRef = useRef(true);
  const kind = useMemo(() => (isUUID(offerId) ? 'bedbank' : 'luxuryescapes'), [offerId]);
  const [globalRanks, setGlobalRanks] = useState<GlobalRanks | null>(null);
  const [fetchingState, setFetchingState] = useState<Utils.FetchingState>('idle');
  const [offerDetails, setOfferDetails] = useState<App.Offer | PublicOfferV2.BedbankOffer | undefined>();
  const [offerScore, setOfferScore] = useState<HotelAdminScore | undefined>();

  useEffect(() => {
    SearchService.getHotelGlobalRanks({ region, brand, isLuxPlusMember }).then(setGlobalRanks);
  }, [brand, isLuxPlusMember, region]);

  useEffect(() => {
    (async () => {
      try {
        setFetchingState('loading');
        const [score, offerDetails] = await Promise.all([
          SearchService.getHotelScoreForVariant(offerId, region, EvVariant.Default),
          kind === 'bedbank'
            ? PublicOfferService.getBedbankOfferById(offerId, region, 'luxuryescapes')
            : OffersService.getOffer(offerId),
        ]);

        if (!isMountedRef.current) {
          return;
        }

        setOfferScore(score.result.score);
        setOfferDetails(offerDetails.result);
        setFetchingState('success');
      } catch (error) {
        setFetchingState('failed');
      }
    })();

    return () => {
      isMountedRef.current = false;
    };
  }, [kind, offerId, region]);

  const expectedValueData: IExpectedValueData = useMemo(() => {
    if (offerScore) {
      const rankingScore = readHotelRankingScore(offerScore);
      return {
        views: rankingScore.viewsCount.toLocaleString(),
        weightedViews: Math.trunc(rankingScore.weightedViewsCount).toLocaleString(),
        salesCount: rankingScore.salesCount.toLocaleString(),
        weightedSalesCount: Math.trunc(rankingScore.weightedSalesCount).toLocaleString(),
        salesAmount: currencyFormatter('AUD', rankingScore.salesAmount),
        marginAmount: currencyFormatter('AUD', Math.round(rankingScore.marginAmount)),
        weightedMarginAmount: currencyFormatter('AUD', Math.trunc(rankingScore.weightedMarginAmount)),
        marginPercentage: rankingScore.marginPercentage,
        mpv: rankingScore.mpv.toFixed(3),
        salesMpv: rankingScore.salesMpv.toFixed(3),
        globalSalesMpv: rankingScore.globalSalesMpv.toFixed(3),
        overrideScore: rankingScore.overrideScore.toFixed(1),
        finalScore: formatFinalScore(rankingScore.overrideScore, rankingScore.salesMpv),
        ctr: (rankingScore.ctr * 100).toFixed(2) + '%',
        info: rankingScore.info,
        globalRank: globalRanks ? globalRanks[offerId] || 'n/a' : undefined,
      };
    }
  }, [offerScore, globalRanks, offerId]);

  const offerType = useMemo(() => {
    if (!offerDetails) {
      return null;
    }
    if ('promotions' in offerDetails && offerDetails.promotions.length > 0) {
      return 'limited_time_special';
    }
    return offerDetails.type;
  }, [offerDetails]);

  const isBedbankType = useMemo(() => {
    return offerType === 'bedbank_hotel' || offerType === 'limited_time_special';
  }, [offerType]);

  const offerName = useMemo<string>(() => {
    if (!offerDetails) {
      return '';
    }

    if (isBedbankType || offerType === 'rental') {
      return offerDetails.name;
    }

    if ('location' in offerDetails) {
      // TODO: INCORRECT TYPE. It is string in runtime, but defined as object in typings
      return String(offerDetails.location);
    }

    return '';
  }, [isBedbankType, offerDetails, offerType]);

  return (
    <>
      {fetchingState == 'loading' && (
        <TableRow>
          <TableCell component="th" colSpan={headerColumnSpan(showDetailed)}>
            Loading...
          </TableCell>
        </TableRow>
      )}
      {fetchingState == 'success' && offerDetails && (
        <TableRow>
          <TableCell align="left">{expectedValueData.globalRank}</TableCell>
          <TableCell align="left" component="th" scope="row" sx={{ maxWidth: 200, width: 200 }}>
            <Box sx={{ maxWidth: 200, overflowWrap: 'anywhere', whiteSpace: 'normal' }}>
              <Link to={isBedbankType ? `/bedbank/properties/${offerId}` : `/offers/${offerId}`}>{offerName}</Link>
            </Box>
          </TableCell>
          <TableCell align="right">{OFFER_TYPE_LABELS[offerType] ?? offerType}</TableCell>
          <TableCell align="right">{expectedValueData.views}</TableCell>
          {showDetailed && <TableCell align="right">{expectedValueData.weightedViews}</TableCell>}
          <TableCell align="right">{expectedValueData.ctr}</TableCell>
          <TableCell align="right">{expectedValueData.salesCount}</TableCell>
          {showDetailed && <TableCell align="right">{expectedValueData.weightedSalesCount}</TableCell>}
          <TableCell align="right">{expectedValueData.salesAmount}</TableCell>
          <TableCell align="right">{expectedValueData.marginAmount}</TableCell>
          {showDetailed && <TableCell align="right">{expectedValueData.weightedMarginAmount}</TableCell>}
          <TableCell align="right">{expectedValueData.marginPercentage}</TableCell>
          <TableCell align="right">{expectedValueData.mpv}</TableCell>
          <FlexTableCell align="right">
            {expectedValueData.salesMpv}
            {expectedValueData.info && (
              <Tooltip title={<RankingInfo info={expectedValueData.info} />} placement="top" arrow>
                <InfoOutlinedIcon fontSize="small" />
              </Tooltip>
            )}
          </FlexTableCell>
          {showDetailed && <TableCell align="right">{expectedValueData.globalSalesMpv}</TableCell>}
          <TableCell align="right">{expectedValueData.overrideScore}</TableCell>
          <TableCell align="right">{expectedValueData.finalScore}</TableCell>
        </TableRow>
      )}
      {fetchingState == 'failed' && (
        <TableRow>
          <TableCell component="th" colSpan={headerColumnSpan(showDetailed)}>
            Failed to load offer: {offerId}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default SearchHotelRow;
